import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { standBezeichnung } from "@dj/domain/revier";
import { Kachel } from "../design/Kachel";
import { Symbol } from "../design/Symbol";
import { useDokument } from "../plattform/lesemodelle";
import { idToken } from "../plattform/sitzung";
import { AnsichtDerDrueckjagd } from "./AnsichtDerDrueckjagd";
import { useRevier, useRevierSammlung } from "./teile";

/**
 * Revierkarte (oberflaeche.md §6, ADR-0022): `region` „Revierkarte“ mit dem Dateinamen der Revierkarte und den Ständen,
 * die die Person sehen darf (Standbesetzung, LIM-0007), als Markierung `button` „Stand <Nr>“ auf der georeferenzierten
 * Fläche; „Vergrößern“, „Verkleinern“ statt Zoomen mit zwei Fingern (N-03) und „Standort“ (eine Positionsabfrage auf
 * Wunsch, N-43). Alles kommt aus den Lesemodellen, die am Jagdtag auf dem Gerät liegen; ohne Netz gibt es keine
 * Grundkarte außerhalb der Revierkarte.
 */
interface Ecke {
  Ecke: string;
  Breite: number;
  Länge: number;
}

interface Ausschnitt {
  nord: number;
  sued: number;
  west: number;
  ost: number;
}

const STUFEN = [1, 1.5, 2, 3, 4];

function ausschnitt(georeferenz: unknown): Ausschnitt | null {
  if (!Array.isArray(georeferenz)) return null;
  const nw = (georeferenz as Ecke[]).find((e) => e.Ecke === "Nordwest");
  const so = (georeferenz as Ecke[]).find((e) => e.Ecke === "Südost");
  if (!nw || !so || nw.Breite <= so.Breite || so.Länge <= nw.Länge) return null;
  return { nord: nw.Breite, sued: so.Breite, west: nw.Länge, ost: so.Länge };
}

/** Lage eines Punktes auf der Fläche in Prozent (links, oben); außerhalb der Revierkarte `null`. */
function lage(a: Ausschnitt, breite: number, laenge: number): { links: number; oben: number } | null {
  const links = ((laenge - a.west) / (a.ost - a.west)) * 100;
  const oben = ((a.nord - breite) / (a.nord - a.sued)) * 100;
  return links < 0 || links > 100 || oben < 0 || oben > 100 ? null : { links, oben };
}

const ERNEUERN_VOR_ABLAUF_MS = 30_000;

/**
 * Kurzlebige Leseadresse der Revierkarte über die Drückjagd (medien.md §6, `{ Drückjagd, Revier, Revierkarte }`); sie
 * wird vor ihrem Ablauf erneuert. Ohne Netz oder ohne Berechtigung `null` — die Ansicht zeigt dann Dateiname und
 * Stände aus den Lesemodellen.
 */
function useRevierkartenadresse(drueckjagd: string, revier: string | null, revierkarte: string | null): string | null {
  const [adresse, setAdresse] = useState<string | null>(null);
  useEffect(() => {
    setAdresse(null);
    if (!revier || !revierkarte) return;
    let aktiv = true;
    let wecker: ReturnType<typeof setTimeout> | undefined;
    async function holen() {
      const t = await idToken();
      try {
        const r = await fetch("/api/medien/abruf", {
          method: "POST", headers: { "Content-Type": "application/json; charset=utf-8", ...(t ? { Authorization: `Bearer ${t}` } : {}) },
          body: JSON.stringify({ "Drückjagd": drueckjagd, Revier: revier, Revierkarte: revierkarte }),
        });
        if (!aktiv || !r.ok) return;
        const k = (await r.json()) as { url?: string; gueltigBis?: string };
        if (!aktiv || !k.url) return;
        setAdresse(k.url);
        const rest = k.gueltigBis ? Date.parse(k.gueltigBis) - Date.now() - ERNEUERN_VOR_ABLAUF_MS : NaN;
        if (Number.isFinite(rest) && rest > 0) wecker = setTimeout(() => void holen(), rest);
      } catch {
        // ohne Netz: keine Adresse; Dateiname und Stände bleiben sichtbar
      }
    }
    void holen();
    return () => { aktiv = false; if (wecker) clearTimeout(wecker); };
  }, [drueckjagd, revier, revierkarte]);
  return adresse;
}

export function RevierkarteDerDrueckjagd() {
  const { id = "" } = useParams();
  const revier = useRevier(id);
  const einweisung = useDokument(revier ? `drueckjagden/${id}/reviere/${revier}/einweisung/aktuell` : null).daten;
  const staende = useRevierSammlung(id, revier, "standbesetzung").daten;
  const karte = einweisung?.["Revierkarte"] as { Datei?: string; Format?: string; Georeferenz?: unknown } | null | undefined;
  const kennung = (einweisung?.["_kennungen"] as Record<string, string> | undefined)?.["Revierkarte"] ?? null;
  const adresse = useRevierkartenadresse(id, revier, kennung);
  const a = ausschnitt(karte?.Georeferenz);
  const [stufe, setStufe] = useState(0);
  const [eigene, setEigene] = useState<{ links: number; oben: number } | null>(null);

  function standort() {
    if (!a || !("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition((p) => setEigene(lage(a, p.coords.latitude, p.coords.longitude)), () => setEigene(null),
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 15_000 });
  }

  const massstab = STUFEN[stufe]!;
  return (
    <AnsichtDerDrueckjagd drueckjagdId={id} name="Revierkarte" flaeche unten={(
      <div className="raster raster--3 liste__unten">
        <Kachel symbol="zoom-aus" begriff="Verkleinern" art="kompakt" isDisabled={stufe === 0} onPress={() => setStufe((s) => Math.max(0, s - 1))} />
        <Kachel symbol="standort" begriff="Standort" art="kompakt" isDisabled={!a} onPress={standort} />
        <Kachel symbol="zoom-ein" begriff="Vergrößern" art="kompakt" isDisabled={stufe === STUFEN.length - 1}
          onPress={() => setStufe((s) => Math.min(STUFEN.length - 1, s + 1))} />
      </div>
    )}>
      <section className="karte platte revierkarte" role="region" aria-label="Revierkarte">
        {!karte?.Datei ? <span className="karte__datei">—</span>
          : karte.Format === "PDF" && adresse
            ? <a className="karte__datei" href={adresse} target="_blank" rel="noopener noreferrer">{karte.Datei}</a>
            : <span className="karte__datei">{karte.Datei}</span>}
        <div className="revierkarte__rahmen">
          <div className="revierkarte__ebene" style={{ width: `${massstab * 100}%`, height: `${massstab * 100}%` }}>
            {adresse && karte?.Format !== "PDF" ? <img className="revierkarte__bild" src={adresse} alt="" /> : null}
            {a ? staende.map((s) => {
              const p = typeof s["Breite"] === "number" && typeof s["Länge"] === "number" ? lage(a, s["Breite"] as number, s["Länge"] as number) : null;
              if (!p) return null;
              const nr = String(s["Stand"]);
              return (
                <button key={s._id} type="button" className="karte__stand" style={{ left: `${p.links}%`, top: `${p.oben}%` }} aria-label={`Stand ${nr}`}>
                  <span className="marke-rund"><Symbol name="stand" /></span>
                  <span className="k__b">{standBezeichnung(nr, s["Standname"] as string | null | undefined)}</span>
                </button>
              );
            }) : null}
            {eigene ? <span className="revierkarte__eigene" style={{ left: `${eigene.links}%`, top: `${eigene.oben}%` }}><Symbol name="standort" /></span> : null}
          </div>
        </div>
      </section>
    </AnsichtDerDrueckjagd>
  );
}
